// app/learn/path/[slug]/community.tsx
// =============================================================================
// Kursi Learn — Community Screen
// Feed, Q&A, AI answers, discussions, leaderboard
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { LEARN_LAUNCH_CONFIG } from '@/constants/learnLaunchConfig';
import { RuntimeAsyncState } from '@/features/unified-learning/runtime/components/RuntimeAsyncState';

// ── Design tokens ─────────────────────────────────────────────────────────────

const BG       = '#0B0B14';
const SURFACE  = '#16142A';
const SURFACE2 = '#221F3D';
const SURFACE3 = '#2D2B50';
const TEXT     = '#F0EFF8';
const TEXT_MED = '#9896B4';
const TEXT_DIM = '#5E5C78';
const PRIMARY  = theme.colors.primary;
const GREEN    = '#10B981';
const AMBER    = '#F59E0B';

const SUPABASE_URL      = process.env.EXPO_PUBLIC_SUPABASE_URL      ?? '';
const SUPABASE_ANON_KEY = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '';

// ── Types ─────────────────────────────────────────────────────────────────────

type PostType = 'question' | 'discussion' | 'tip' | 'success';
type Filter = 'all' | 'question' | 'tip' | 'success';

type Post = {
  id: string;
  post_type: PostType;
  title: string;
  body: string;
  display_name: string;
  created_at: string;
  likes_count: number;
  replies_count: number;
  is_resolved: boolean;
  is_pinned: boolean;
  ai_answer: string | null;
  user_liked: boolean;
};

const POST_TYPE_CONFIG: Record<PostType, { label: string; icon: string; color: string }> = {
  question:   { label: 'שאלה',         icon: '?', color: PRIMARY },
  discussion: { label: 'דיון',          icon: '⊙', color: AMBER },
  tip:        { label: 'טיפ',           icon: '◈', color: GREEN },
  success:    { label: 'הצלחה',         icon: '✓', color: '#F472B6' },
};

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all',      label: 'הכל' },
  { key: 'question', label: 'שאלות' },
  { key: 'tip',      label: 'טיפים' },
  { key: 'success',  label: 'הצלחות' },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function CommunityScreen() {
  const params  = useLocalSearchParams<{ slug: string; uid: string; worldId?: string }>();
  const slug    = typeof params.slug    === 'string' ? params.slug    : '';
  const userId  = typeof params.uid     === 'string' ? params.uid     : '';
  const worldId = typeof params.worldId === 'string' ? params.worldId : '';

  const [posts, setPosts]       = useState<Post[]>([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState<Filter>('all');
  const [composing, setComposing] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody]   = useState('');
  const [newType, setNewType]   = useState<PostType>('question');
  const [posting, setPosting]   = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [replies, setReplies]   = useState<any[]>([]);
  const [replyBody, setReplyBody] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-community`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({ action: 'list', world_id: worldId, user_id: userId, filter, limit: 30 }),
      });
      const data = await res.json();
      setPosts(data.posts ?? []);
    } finally {
      setLoading(false);
    }
  }, [worldId, userId, filter]);

  useEffect(() => { if (worldId && userId) loadPosts(); }, [worldId, userId, filter, loadPosts]);

  const openPost = async (post: Post) => {
    setSelectedPost(post);
    setReplies([]);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-community`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({ action: 'get_post', post_id: post.id, user_id: userId }),
      });
      const data = await res.json();
      setSelectedPost(data.post ?? post);
      setReplies(data.replies ?? []);
    } catch { /* non-fatal */ }
  };

  const likePost = async (post: Post) => {
    // Optimistic update
    setPosts((prev) =>
      prev.map((p) => p.id === post.id
        ? { ...p, user_liked: !p.user_liked, likes_count: p.user_liked ? p.likes_count - 1 : p.likes_count + 1 }
        : p,
      ),
    );
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/kp-community`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({ action: 'like', target_id: post.id, target_type: 'post', user_id: userId }),
      });
    } catch { /* non-fatal */ }
  };

  const submitPost = async () => {
    if (!newTitle.trim() || !newBody.trim()) return;
    setPosting(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-community`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({
          action:       'post',
          world_id:     worldId,
          user_id:      userId,
          display_name: 'לומד',
          post_type:    newType,
          title:        newTitle.trim(),
          body:         newBody.trim(),
        }),
      });
      const data = await res.json();
      if (data.post) {
        setPosts((prev) => [data.post, ...prev]);
        setNewTitle('');
        setNewBody('');
        setComposing(false);
      }
    } finally {
      setPosting(false);
    }
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !selectedPost) return;
    setReplyLoading(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-community`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'apikey': SUPABASE_ANON_KEY },
        body:    JSON.stringify({
          action: 'reply', post_id: selectedPost.id, user_id: userId,
          display_name: 'לומד', body: replyBody.trim(),
        }),
      });
      const data = await res.json();
      if (data.reply) {
        setReplies((prev) => [...prev, data.reply]);
        setReplyBody('');
      }
    } finally {
      setReplyLoading(false);
    }
  };

  if (!LEARN_LAUNCH_CONFIG.advancedTools.community) {
    return (
      <RuntimeAsyncState
        state="degraded"
        title="קהילה זמינה בהמשך"
        description="בטא זו מתמקדת כרגע בלולאת הליבה: שיעור → תרגול → סקירה → מבחן. קהילה תחזור בהשקה מדורגת עם חוזה אמינות מלא."
        actionLabel="חזרה לבית המסלול"
        onAction={() => {
          if (router.canGoBack()) router.back();
          else router.replace(slug ? `/learn/path/${slug}/home` as any : '/learn' as any);
        }}
      />
    );
  }

  // ── Post detail view ──────────────────────────────────────────────────────

  if (selectedPost) {
    const tc = POST_TYPE_CONFIG[selectedPost.post_type];
    return (
      <KeyboardAvoidingView
        style={s.root}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={88}
      >
        <View style={s.header}>
          <Pressable onPress={() => setSelectedPost(null)} style={s.backBtn}>
            <Text style={s.backText}>‹ חזרה</Text>
          </Pressable>
          <Text style={s.headerTitle}>דיון</Text>
          <View style={{ width: 64 }} />
        </View>

        <ScrollView style={{ flex: 1 }} contentContainerStyle={pd.content} showsVerticalScrollIndicator={false}>
          <View style={[pd.card, { borderColor: tc.color + '2A' }]}>
            <View style={pd.typeRow}>
              <View style={[pd.typeBadge, { backgroundColor: tc.color + '14', borderColor: tc.color + '33' }]}>
                <Text style={pd.typeBadgeIcon}>{tc.icon}</Text>
                <Text style={[pd.typeBadgeText, { color: tc.color }]}>{tc.label}</Text>
              </View>
              <Text style={pd.author}>{selectedPost.display_name}</Text>
            </View>
            <Text style={pd.title}>{selectedPost.title}</Text>
            <Text style={pd.body}>{selectedPost.body}</Text>
            <Text style={pd.time}>{new Date(selectedPost.created_at).toLocaleDateString('he-IL')}</Text>
          </View>

          {/* AI Answer */}
          {selectedPost.ai_answer && (
            <View style={pd.aiCard}>
              <View style={pd.aiHeader}>
                <Text style={pd.aiLabel}>תשובת מנחה</Text>
              </View>
              <Text style={pd.aiText}>{selectedPost.ai_answer}</Text>
            </View>
          )}

          {/* Replies */}
          {replies.length > 0 && (
            <>
              <Text style={pd.repliesTitle}>תגובות ({replies.length})</Text>
              {replies.map((r) => (
                <View key={r.id} style={pd.replyRow}>
                  {r.is_ai && <Text style={pd.replyAiBadge}>מנחה</Text>}
                  <Text style={pd.replyAuthor}>{r.display_name}</Text>
                  <Text style={pd.replyBody}>{r.body}</Text>
                </View>
              ))}
            </>
          )}

          <View style={{ height: 16 }} />
        </ScrollView>

        {/* Reply input */}
        <View style={pd.replyBar}>
          <TextInput
            style={pd.replyInput}
            value={replyBody}
            onChangeText={setReplyBody}
            placeholder="הוסף תגובה..."
            placeholderTextColor={TEXT_DIM}
            textAlign="right"
            multiline
            maxLength={500}
          />
          <Pressable
            style={[pd.replySend, { backgroundColor: replyBody.trim() ? PRIMARY : SURFACE3 }]}
            onPress={sendReply}
            disabled={!replyBody.trim() || replyLoading}
          >
            {replyLoading
              ? <ActivityIndicator size="small" color={TEXT_MED} />
              : <Text style={pd.replySendText}>›</Text>
            }
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    );
  }

  // ── Feed view ─────────────────────────────────────────────────────────────

  return (
    <KeyboardAvoidingView
      style={s.root}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={88}
    >
      <View style={s.header}>
        <Pressable onPress={() => router.back()} style={s.backBtn}>
          <Text style={s.backText}>‹ חזרה</Text>
        </Pressable>
        <Text style={s.headerTitle}>קהילה</Text>
        <Pressable style={s.newPostBtn} onPress={() => setComposing((v) => !v)}>
          <Text style={s.newPostText}>{composing ? '✕' : '+ חדש'}</Text>
        </Pressable>
      </View>

      {/* Compose form */}
      {composing && (
        <View style={s.composeCard}>
          {/* Post type selector */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 10 }}
            contentContainerStyle={{ gap: 8, flexDirection: 'row-reverse' }}>
            {(Object.keys(POST_TYPE_CONFIG) as PostType[]).map((pt) => {
              const c = POST_TYPE_CONFIG[pt];
              return (
                <Pressable
                  key={pt}
                  style={[s.typeChip, newType === pt && { backgroundColor: c.color + '1A', borderColor: c.color + '44' }]}
                  onPress={() => setNewType(pt)}
                >
                  <Text style={s.typeChipIcon}>{c.icon}</Text>
                  <Text style={[s.typeChipText, newType === pt && { color: c.color }]}>{c.label}</Text>
                </Pressable>
              );
            })}
          </ScrollView>
          <TextInput
            style={s.composeTitle}
            value={newTitle}
            onChangeText={setNewTitle}
            placeholder="כותרת..."
            placeholderTextColor={TEXT_DIM}
            textAlign="right"
            maxLength={200}
          />
          <TextInput
            style={s.composeBody}
            value={newBody}
            onChangeText={setNewBody}
            placeholder="פרטים, הקשר, שאלה..."
            placeholderTextColor={TEXT_DIM}
            textAlign="right"
            multiline
            maxLength={1000}
          />
          <Pressable
            style={[s.postBtn, (posting || !newTitle.trim() || !newBody.trim()) && { opacity: 0.5 }]}
            onPress={submitPost}
            disabled={posting || !newTitle.trim() || !newBody.trim()}
          >
            {posting
              ? <ActivityIndicator size="small" color="#fff" />
              : <Text style={s.postBtnText}>פרסם</Text>
            }
          </Pressable>
        </View>
      )}

      {/* Filter bar */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={s.filterBar}
        contentContainerStyle={s.filterContent}
      >
        {FILTERS.map((f) => (
          <Pressable
            key={f.key}
            style={[s.filterChip, filter === f.key && { backgroundColor: PRIMARY + '1A', borderColor: PRIMARY + '44' }]}
            onPress={() => setFilter(f.key)}
          >
            <Text style={[s.filterText, filter === f.key && { color: PRIMARY }]}>{f.label}</Text>
          </Pressable>
        ))}
      </ScrollView>

      {/* Posts */}
      {loading ? (
        <View style={s.centered}>
          <ActivityIndicator color={PRIMARY} size="large" />
        </View>
      ) : posts.length === 0 ? (
        <View style={s.empty}>
          <Text style={s.emptyTitle}>הקהילה מחכה לפוסט הראשון</Text>
          <Text style={s.emptySub}>שאלו שאלה, שתפו טיפ, או ספרו על הצלחה שלכם.</Text>
        </View>
      ) : (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={s.feed} showsVerticalScrollIndicator={false}>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onOpen={() => openPost(post)}
              onLike={() => likePost(post)}
            />
          ))}
          <View style={{ height: 24 }} />
        </ScrollView>
      )}
    </KeyboardAvoidingView>
  );
}

// ── Post card ─────────────────────────────────────────────────────────────────

function PostCard({ post, onOpen, onLike }: { post: Post; onOpen: () => void; onLike: () => void }) {
  const tc = POST_TYPE_CONFIG[post.post_type];
  return (
    <Pressable style={pc.card} onPress={onOpen} accessibilityRole="button">
      <View style={pc.topRow}>
        <View style={[pc.typeBadge, { backgroundColor: tc.color + '14', borderColor: tc.color + '2A' }]}>
          <Text style={pc.typeBadgeIcon}>{tc.icon}</Text>
          <Text style={[pc.typeBadgeText, { color: tc.color }]}>{tc.label}</Text>
        </View>
        {post.is_resolved && (
          <View style={pc.resolvedBadge}>
            <Text style={pc.resolvedText}>✓ נפתר</Text>
          </View>
        )}
        {post.ai_answer && (
          <View style={pc.aiBadge}>
            <Text style={pc.aiText}>תשובת מנחה</Text>
          </View>
        )}
      </View>

      <Text style={pc.title} numberOfLines={2}>{post.title}</Text>
      <Text style={pc.body} numberOfLines={2}>{post.body}</Text>

      <View style={pc.footer}>
        <Text style={pc.author}>{post.display_name}</Text>
        <Text style={pc.time}>{new Date(post.created_at).toLocaleDateString('he-IL')}</Text>
        <View style={{ flex: 1 }} />
        <Pressable style={pc.likeBtn} onPress={(e) => { e.stopPropagation?.(); onLike(); }} accessibilityRole="button">
          <Text style={[pc.likeIcon, post.user_liked && { color: '#F472B6' }]}>
            {post.user_liked ? '♥' : '♡'}
          </Text>
          {post.likes_count > 0 && <Text style={pc.likeCount}>{post.likes_count}</Text>}
        </Pressable>
        {post.replies_count > 0 && (
          <View style={pc.repliesIndicator}>
            <Text style={pc.repliesText}>{post.replies_count} תגובות</Text>
          </View>
        )}
      </View>
    </Pressable>
  );
}

const pc = StyleSheet.create({
  card: {
    backgroundColor: SURFACE, borderRadius: 16, borderWidth: 1, borderColor: SURFACE3,
    padding: 14, gap: 8,
  },
  topRow: { flexDirection: 'row-reverse', alignItems: 'center', gap: 6, flexWrap: 'wrap' as any },
  typeBadge: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 4,
    borderRadius: 99, borderWidth: 1, paddingHorizontal: 8, paddingVertical: 3,
  },
  typeBadgeIcon: { fontSize: 11 },
  typeBadgeText: { fontFamily: theme.fonts.bold, fontSize: 11 },
  resolvedBadge: {
    backgroundColor: GREEN + '14', borderRadius: 99, borderWidth: 1, borderColor: GREEN + '33',
    paddingHorizontal: 8, paddingVertical: 3,
  },
  resolvedText: { fontFamily: theme.fonts.bold, fontSize: 10, color: GREEN },
  aiBadge: {
    backgroundColor: PRIMARY + '10', borderRadius: 99, borderWidth: 1, borderColor: PRIMARY + '22',
    paddingHorizontal: 8, paddingVertical: 3,
  },
  aiText: { fontFamily: theme.fonts.regular, fontSize: 10, color: PRIMARY },
  title:  { fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT, textAlign: 'right', lineHeight: 20 },
  body:   { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right', lineHeight: 18 },
  footer: { flexDirection: 'row-reverse', alignItems: 'center', gap: 8 },
  author: { fontFamily: theme.fonts.medium, fontSize: 11, color: TEXT_DIM },
  time:   { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_DIM },
  likeBtn: { flexDirection: 'row-reverse', alignItems: 'center', gap: 3, padding: 4 },
  likeIcon: { fontSize: 16, color: TEXT_DIM },
  likeCount: { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_DIM },
  repliesIndicator: { paddingHorizontal: 6 },
  repliesText: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_DIM },
});

// Post detail styles
const pd = StyleSheet.create({
  content: {
    paddingHorizontal: 16, paddingTop: 16,
    maxWidth: 600, alignSelf: 'center' as any, width: '100%', gap: 12,
  },
  card: {
    backgroundColor: SURFACE, borderRadius: 18, borderWidth: 1, padding: 18, gap: 10,
  },
  typeRow: { flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'center' },
  typeBadge: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 5,
    borderRadius: 99, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 4,
  },
  typeBadgeIcon: { fontSize: 13 },
  typeBadgeText: { fontFamily: theme.fonts.bold, fontSize: 12 },
  author: { fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_DIM },
  title:  { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT, textAlign: 'right', lineHeight: 26 },
  body:   { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED, textAlign: 'right', lineHeight: 22 },
  time:   { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_DIM, textAlign: 'right' },
  aiCard: {
    backgroundColor: PRIMARY + '0A', borderRadius: 14, borderWidth: 1, borderColor: PRIMARY + '22',
    padding: 14, gap: 8,
  },
  aiHeader: { flexDirection: 'row-reverse', alignItems: 'center', gap: 6 },
  aiIcon:   { fontSize: 16 },
  aiLabel:  { fontFamily: theme.fonts.bold, fontSize: 12, color: PRIMARY },
  aiText:   { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT, textAlign: 'right', lineHeight: 22 },
  repliesTitle: {
    fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_DIM,
    textTransform: 'uppercase' as any, letterSpacing: 0.5, textAlign: 'right',
  },
  replyRow: {
    backgroundColor: SURFACE, borderRadius: 12, borderWidth: 1, borderColor: SURFACE3,
    padding: 12, gap: 4,
  },
  replyAiBadge: { fontFamily: theme.fonts.bold, fontSize: 10, color: PRIMARY, textAlign: 'right' },
  replyAuthor:  { fontFamily: theme.fonts.bold, fontSize: 11, color: TEXT_MED, textAlign: 'right' },
  replyBody:    { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT, textAlign: 'right', lineHeight: 20 },
  replyBar: {
    flexDirection: 'row-reverse', alignItems: 'flex-end', gap: 8,
    paddingHorizontal: 16, paddingVertical: 10,
    paddingBottom: Platform.OS === 'ios' ? 28 : 12,
    borderTopWidth: 1, borderTopColor: SURFACE3, backgroundColor: BG,
  },
  replyInput: {
    flex: 1, backgroundColor: SURFACE, borderWidth: 1, borderColor: SURFACE3,
    borderRadius: 16, paddingHorizontal: 14, paddingVertical: 10,
    fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT,
    maxHeight: 80, textAlign: 'right',
  },
  replySend: {
    width: 40, height: 40, borderRadius: 20,
    alignItems: 'center', justifyContent: 'center',
  },
  replySendText: { color: '#fff', fontSize: 20, fontWeight: '700' as any },
});

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root:    { flex: 1, backgroundColor: BG },
  centered: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
  },

  header: {
    flexDirection: 'row-reverse', alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'web' ? 20 : 16,
    paddingBottom: 12,
    borderBottomWidth: 1, borderBottomColor: SURFACE3,
  },
  backBtn:     { paddingHorizontal: 8, paddingVertical: 6, minWidth: 64 },
  backText:    { color: PRIMARY, fontFamily: theme.fonts.medium, fontSize: 15 },
  headerTitle: { flex: 1, color: TEXT, fontFamily: theme.fonts.bold, fontSize: 17, textAlign: 'center' },
  newPostBtn:  { paddingHorizontal: 8, paddingVertical: 6, minWidth: 64, alignItems: 'flex-end' },
  newPostText: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 14 },

  composeCard: {
    backgroundColor: SURFACE, borderBottomWidth: 1, borderBottomColor: SURFACE3,
    padding: 16, gap: 10,
  },
  typeChip: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 5,
    borderRadius: 99, borderWidth: 1, borderColor: SURFACE3,
    paddingHorizontal: 12, paddingVertical: 7, backgroundColor: SURFACE2,
  },
  typeChipIcon: { fontSize: 13 },
  typeChipText: { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT_MED },
  composeTitle: {
    backgroundColor: SURFACE2, borderWidth: 1, borderColor: SURFACE3,
    borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10,
    fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT,
  },
  composeBody: {
    backgroundColor: SURFACE2, borderWidth: 1, borderColor: SURFACE3,
    borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10,
    fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT,
    minHeight: 80, textAlignVertical: 'top' as any,
  },
  postBtn: {
    backgroundColor: PRIMARY, borderRadius: 10, paddingVertical: 12, alignItems: 'center',
  },
  postBtnText: { fontFamily: theme.fonts.bold, fontSize: 14, color: '#fff' },

  filterBar: { maxHeight: 50 },
  filterContent: {
    paddingHorizontal: 16, gap: 8, paddingVertical: 10,
    flexDirection: 'row-reverse',
  },
  filterChip: {
    borderRadius: 99, borderWidth: 1, borderColor: SURFACE3,
    paddingHorizontal: 14, paddingVertical: 6, backgroundColor: SURFACE2,
  },
  filterText: { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT_MED },

  feed: {
    paddingHorizontal: 16, paddingTop: 12,
    maxWidth: 600, alignSelf: 'center' as any, width: '100%', gap: 10,
  },

  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32 },
  emptyIcon:  { fontSize: 48 },
  emptyTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT, textAlign: 'center' },
  emptySub:   { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED, textAlign: 'center', lineHeight: 22 },
});
